import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, MapPin, ChevronDown, ChevronRight as ChevronRightIcon } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, DataTable, Input, Textarea, Select } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";

const empty = () => ({
  slug: "",
  name: "",
  category: "",
  developer: "",
  lat: 41.20,
  lng: 28.68,
  district: "Arnavutköy",
  region: "İstanbul",
  hero_kicker: "",
  hero_image: "",
  summary: "",
  overview_h2: "Proje Hakkında",
  overview_body: "",
  location_h2: "Konum & Ulaşım",
  location_body: "",
  location_advantages: [],
  features_h2: "Öne Çıkan Özellikler",
  features: [],
  gallery_h2: "Galeri",
  gallery: [],
  meta_title: "",
  meta_description: "",
  keywords: [],
  h1: "",
  og_image: "",
  canonical: "",
  noindex: false,
  schema_type: "Place",
  google_maps_url: "",
  boundary_geojson: "",
  boundary_color: "#22346B",
  boundary_fill: "#22346B",
  boundary_fill_opacity: 0.18,
  layer_group: "Devam Eden Projeler",
  featured: false,
  status: "published",
  order: 0,
});

/* --- Reusable string-array editor (chips) --- */
const StringListEditor = ({ label, value, onChange, testId, placeholder = "Ekle ve Enter" }) => {
  const list = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...list, v]);
    setDraft("");
  };
  return (
    <div>
      {label ? <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">{label}</div> : null}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {list.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-brand-navy/5 text-brand-navy text-xs px-2 py-1">
            {item}
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="hover:text-red-600" aria-label="Sil">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 bg-white border border-black/15 focus:border-brand-navy focus:outline-none px-3 py-2 text-sm"
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <button type="button" onClick={add} className="px-3 py-2 border border-brand-navy text-xs uppercase tracking-[0.2em] hover:bg-brand-navy hover:text-white" data-testid={testId ? `${testId}-add` : undefined}>Ekle</button>
      </div>
    </div>
  );
};

/* --- Features editor (icon + title + description) --- */
const FeaturesEditor = ({ value, onChange }) => {
  const list = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(list.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const add = () => onChange([...list, { icon: "★", title: "", description: "" }]);
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  return (
    <div className="space-y-3">
      {list.map((f, i) => (
        <div key={i} className="grid grid-cols-[80px_1fr_2fr_40px] gap-3 items-start" data-testid={`feature-row-${i}`}>
          <input value={f.icon || ""} onChange={(e) => update(i, { icon: e.target.value })} placeholder="⭐" className="border border-black/15 px-3 py-2 text-lg text-center" />
          <input value={f.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="Başlık (H3)" className="border border-black/15 px-3 py-2 text-sm" />
          <input value={f.description || ""} onChange={(e) => update(i, { description: e.target.value })} placeholder="Açıklama" className="border border-black/15 px-3 py-2 text-sm" />
          <button type="button" onClick={() => remove(i)} className="text-red-500 hover:text-red-700 p-2" aria-label="Sil"><Trash2 size={16} /></button>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-brand-navy px-4 py-2 hover:bg-brand-navy hover:text-white" data-testid="feature-add">
        <Plus size={14} /> Özellik Ekle
      </button>
    </div>
  );
};

/* --- Gallery editor (multi image upload) --- */
const GalleryEditor = ({ value, onChange }) => {
  const list = Array.isArray(value) ? value : [];
  const add = (url) => onChange([...list, url]);
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {list.map((url, i) => (
          <div key={i} className="relative group">
            <img
              src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`}
              alt=""
              className="w-full h-24 object-cover"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 bg-white/90 border border-black/10 p-1 opacity-0 group-hover:opacity-100 transition"
              aria-label="Sil"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
      <ImageUpload label="Galeriye Görsel Ekle" value="" onChange={(u) => u && add(u)} testId="gallery-upload" />
    </div>
  );
};

const Section = ({ title, initialOpen = true, children, testId }) => {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div className="border border-black/10" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 bg-brand-light hover:bg-brand-light/70 text-left"
      >
        <span className="font-serif text-brand-navy">{title}</span>
        {open ? <ChevronDown size={16} /> : <ChevronRightIcon size={16} />}
      </button>
      {open ? <div className="p-5 space-y-4 border-t border-black/10">{children}</div> : null}
    </div>
  );
};

const slugify = (str) => (str || "")
  .toString()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/ı/gi, "i").replace(/ş/gi, "s").replace(/ğ/gi, "g").replace(/ç/gi, "c").replace(/ü/gi, "u").replace(/ö/gi, "o")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const AdminYenisehirProjectsPage = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-generate slug from name unless the user manually edited it
  useEffect(() => {
    if (editing && !slugTouched && !editing?.slug && form.name) {
      setForm((f) => ({ ...f, slug: slugify(f.name) }));
    }
  }, [form.name, editing, slugTouched]);

  const load = () => api.get("/admin/yenisehir-projects").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ new: true }); setForm(empty()); setSlugTouched(false); };
  const startEdit = (row) => {
    const merged = { ...empty(), ...row };
    merged.features = Array.isArray(row.features) ? row.features : [];
    merged.location_advantages = Array.isArray(row.location_advantages) ? row.location_advantages : [];
    merged.keywords = Array.isArray(row.keywords) ? row.keywords : [];
    merged.gallery = Array.isArray(row.gallery) ? row.gallery : [];
    setEditing(row);
    setForm(merged);
    setSlugTouched(true);  // don't overwrite existing slug
  };
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        lat: parseFloat(form.lat) || 0,
        lng: parseFloat(form.lng) || 0,
        order: parseInt(form.order, 10) || 0,
      };
      if (editing?.new) await api.post("/admin/yenisehir-projects", payload);
      else await api.put(`/admin/yenisehir-projects/${editing.id}`, payload);
      toast.success("Kaydedildi");
      cancel();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`${row.name} silinsin mi?`)) return;
    try {
      await api.delete(`/admin/yenisehir-projects/${row.id}`);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div data-testid="admin-yenisehir-projects-page">
      <AdminHeader
        eyebrow="Yenişehir"
        title="Yenişehir Projeleri"
        action={<Btn onClick={startNew} testId="new-yenisehir-project-btn"><Plus size={14} strokeWidth={1.5} /> Yeni Proje</Btn>}
      />
      <p className="text-sm text-brand-medium mb-6 max-w-3xl">
        Her proje; kendine ait <strong>ayrı bir SEO sayfası</strong> (<code className="text-xs bg-brand-light px-1">/yenisehir-projeleri/&lt;slug&gt;</code>), H1/H2/H3 kontrolü ve JSON-LD zenginleştirmesi ile indekslenir. Bu projeler ayrıca <code className="text-xs bg-brand-light px-1">/yenisehir</code> haritasında altın ikon olarak görünür.
      </p>

      <DataTable
        rows={rows}
        columns={[
          { key: "hero_image", label: "", render: (r) => r.hero_image ? <img src={r.hero_image.startsWith("http") ? r.hero_image : `${process.env.REACT_APP_BACKEND_URL}${r.hero_image}`} alt="" className="w-14 h-10 object-cover" /> : <div className="w-14 h-10 bg-brand-light flex items-center justify-center"><MapPin size={14} className="text-brand-medium" /></div> },
          { key: "order", label: "#", render: (r) => <span className="tabular-nums text-brand-medium">{r.order}</span> },
          { key: "name", label: "Proje", render: (r) => <div><div className="font-medium text-brand-navy">{r.name}</div><div className="text-xs text-brand-medium">/{r.slug}</div></div> },
          { key: "category", label: "Kategori" },
          { key: "developer", label: "Geliştirici", render: (r) => <span className="text-xs text-brand-medium">{r.developer}</span> },
          { key: "coords", label: "Koordinat", render: (r) => <span className="text-xs font-mono tabular-nums text-brand-medium">{Number(r.lat).toFixed(4)}, {Number(r.lng).toFixed(4)}</span> },
          { key: "status", label: "Durum", render: (r) => <span className={`text-xs px-2 py-0.5 ${r.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.status === "published" ? "Yayında" : "Taslak"}</span> },
          {
            key: "actions", label: "", render: (r) => (
              <div className="flex items-center gap-1">
                <a href={`/yenisehir-projeleri/${r.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 border border-black/10 hover:border-brand-gold hover:text-brand-gold" data-testid={`view-yenisehir-${r.slug}`}>Aç</a>
                <button onClick={() => startEdit(r)} className="text-xs px-3 py-1.5 border border-black/10 hover:border-brand-navy" data-testid={`edit-yenisehir-${r.slug}`}>Düzenle</button>
                <button onClick={() => remove(r)} className="text-red-600 hover:text-red-700 p-2" data-testid={`delete-yenisehir-${r.slug}`}><Trash2 size={14} strokeWidth={1.5} /></button>
              </div>
            ),
          },
        ]}
      />

      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="yenisehir-editor-modal">
          <div className="absolute inset-0 bg-black/40" onClick={cancel} />
          <form onSubmit={save} className="relative bg-white w-full max-w-4xl max-h-[92vh] shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 px-6 py-4 border-b border-black/10 flex items-center justify-between bg-white">
              <div>
                <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-primary font-medium">Yenişehir Proje</div>
                <div className="font-serif text-lg text-brand-navy">{editing?.new ? "Yeni Proje" : form.name}</div>
              </div>
              <div className="flex items-center gap-3">
                <Btn type="submit" testId="save-yenisehir-btn" disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</Btn>
                <button type="button" onClick={cancel} className="text-brand-medium hover:text-brand-navy text-xl">×</button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Section 1: Temel Bilgi */}
              <Section title="① Temel Bilgi" testId="section-basic">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Proje Adı" value={form.name} onChange={(v) => set("name", v)} required testId="yenisehir-name" />
                  <Input label="Slug (URL — otomatik)" value={form.slug} onChange={(v) => { setSlugTouched(true); set("slug", slugify(v)); }} placeholder="baklali-toki" testId="yenisehir-slug" />
                  <Input label="Kategori" value={form.category} onChange={(v) => set("category", v)} placeholder="Mega Konut Projesi" testId="yenisehir-category" />
                  <Input label="Geliştirici" value={form.developer} onChange={(v) => set("developer", v)} placeholder="TOKİ" testId="yenisehir-developer" />
                  <Input label="Enlem (lat)" type="number" value={form.lat} onChange={(v) => set("lat", v)} testId="yenisehir-lat" />
                  <Input label="Boylam (lng)" type="number" value={form.lng} onChange={(v) => set("lng", v)} testId="yenisehir-lng" />
                  <Input label="İlçe" value={form.district} onChange={(v) => set("district", v)} testId="yenisehir-district" />
                  <Input label="Bölge" value={form.region} onChange={(v) => set("region", v)} testId="yenisehir-region" />
                  <Input label="Sıra" type="number" value={form.order} onChange={(v) => set("order", v)} testId="yenisehir-order" />
                  <Select
                    label="Durum"
                    value={form.status}
                    onChange={(v) => set("status", v)}
                    options={[{ value: "published", label: "Yayında" }, { value: "draft", label: "Taslak" }]}
                    testId="yenisehir-status"
                  />
                  <label className="flex items-center gap-2 text-sm mt-6">
                    <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="yenisehir-featured" />
                    Öne Çıkan
                  </label>
                </div>
              </Section>

              {/* Section 2: Hero */}
              <Section title="② Hero Bölümü" testId="section-hero">
                <Input label="Kicker (üst etiket)" value={form.hero_kicker} onChange={(v) => set("hero_kicker", v)} placeholder="Yenişehir · Etap 1" testId="yenisehir-hero-kicker" />
                <ImageUpload label="Hero Görseli" value={form.hero_image} onChange={(v) => set("hero_image", v)} testId="yenisehir-hero-image" />
                <Textarea label="Özet (lead paragraph)" value={form.summary} onChange={(v) => set("summary", v)} rows={3} testId="yenisehir-summary" />
              </Section>

              {/* Section 3: Overview */}
              <Section title="③ Proje Hakkında (Overview)" testId="section-overview">
                <Input label="H2 Başlık" value={form.overview_h2} onChange={(v) => set("overview_h2", v)} testId="yenisehir-overview-h2" />
                <Textarea label="İçerik (paragrafları boş satır ile ayırın)" value={form.overview_body} onChange={(v) => set("overview_body", v)} rows={8} testId="yenisehir-overview-body" />
              </Section>

              {/* Section 4: Location */}
              <Section title="④ Konum & Ulaşım" initialOpen={false} testId="section-location">
                <Input label="H2 Başlık" value={form.location_h2} onChange={(v) => set("location_h2", v)} testId="yenisehir-location-h2" />
                <Textarea label="Konum Açıklaması" value={form.location_body} onChange={(v) => set("location_body", v)} rows={5} testId="yenisehir-location-body" />
                <StringListEditor label="Konum Avantajları (madde madde)" value={form.location_advantages} onChange={(v) => set("location_advantages", v)} testId="yenisehir-location-advantages" placeholder="Örn: İstanbul Havalimanı'na 15 dk" />
              </Section>

              {/* Section 5: Features */}
              <Section title="⑤ Öne Çıkan Özellikler (H3 kartları)" initialOpen={false} testId="section-features">
                <Input label="H2 Başlık" value={form.features_h2} onChange={(v) => set("features_h2", v)} testId="yenisehir-features-h2" />
                <FeaturesEditor value={form.features} onChange={(v) => set("features", v)} />
              </Section>

              {/* Section 6: Gallery */}
              <Section title="⑥ Galeri" initialOpen={false} testId="section-gallery">
                <Input label="H2 Başlık" value={form.gallery_h2} onChange={(v) => set("gallery_h2", v)} testId="yenisehir-gallery-h2" />
                <GalleryEditor value={form.gallery} onChange={(v) => set("gallery", v)} />
              </Section>

              {/* Section 6b: Harita Katmanı */}
              <Section title="⑥b Harita Katmanı (GeoJSON Sınırı)" initialOpen={false} testId="section-boundary">
                <p className="text-xs text-brand-medium">
                  Proje sınırlarını haritada renkli poligon olarak göstermek için GeoJSON metnini yapıştırın.
                  Feature veya FeatureCollection formatı desteklenir. Boş bırakırsanız harita üzerinde sadece işaretçi (nokta) gösterilir.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Katman Grubu"
                    value={form.layer_group}
                    onChange={(v) => set("layer_group", v)}
                    options={[
                      { value: "Devam Eden Projeler", label: "Devam Eden Projeler" },
                      { value: "Planlanan Projeler", label: "Planlanan Projeler" },
                      { value: "Altyapı Projeleri", label: "Altyapı Projeleri" },
                      { value: "Konut Projeleri", label: "Konut Projeleri" },
                      { value: "Ticari & Sanayi", label: "Ticari & Sanayi" },
                    ]}
                    testId="yenisehir-layer-group"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">Çerçeve</div>
                      <input type="color" value={form.boundary_color} onChange={(e) => set("boundary_color", e.target.value)} className="w-full h-10 border border-black/15 cursor-pointer" data-testid="yenisehir-boundary-color" />
                    </div>
                    <div>
                      <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">Dolgu</div>
                      <input type="color" value={form.boundary_fill} onChange={(e) => set("boundary_fill", e.target.value)} className="w-full h-10 border border-black/15 cursor-pointer" data-testid="yenisehir-boundary-fill" />
                    </div>
                    <div>
                      <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">Opaklık</div>
                      <input type="number" step="0.05" min="0" max="1" value={form.boundary_fill_opacity} onChange={(e) => set("boundary_fill_opacity", parseFloat(e.target.value))} className="w-full h-10 border border-black/15 px-3 text-sm" data-testid="yenisehir-boundary-opacity" />
                    </div>
                  </div>
                </div>
                <Textarea
                  label="GeoJSON İçeriği"
                  value={form.boundary_geojson}
                  onChange={(v) => set("boundary_geojson", v)}
                  rows={10}
                  placeholder='{"type":"Feature","geometry":{"type":"Polygon","coordinates":[[[28.68,41.20],[28.69,41.20],[28.69,41.21],[28.68,41.21],[28.68,41.20]]]}}'
                  testId="yenisehir-boundary-geojson"
                />
                <div className="text-xs text-brand-medium">
                  💡 GeoJSON.io veya QGIS ile poligon çizip export edebilirsiniz. Yalnızca WGS-84 (EPSG:4326) koordinatları desteklenir.
                </div>
              </Section>

              {/* Section 7: SEO */}
              <Section title="⑦ SEO & Meta" initialOpen={false} testId="section-seo">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Meta Title (browser tab)" value={form.meta_title} onChange={(v) => set("meta_title", v)} placeholder="Boş bırakılırsa proje adı kullanılır" testId="yenisehir-meta-title" />
                  <Input label="H1 Override" value={form.h1} onChange={(v) => set("h1", v)} placeholder="Boş bırakılırsa proje adı kullanılır" testId="yenisehir-h1" />
                </div>
                <Textarea label="Meta Description (150-160 karakter)" value={form.meta_description} onChange={(v) => set("meta_description", v)} rows={3} testId="yenisehir-meta-desc" />
                <StringListEditor label="Anahtar Kelimeler" value={form.keywords} onChange={(v) => set("keywords", v)} testId="yenisehir-keywords" placeholder="Örn: Baklalı TOKİ" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ImageUpload label="OG Görsel (sosyal medya paylaşımı)" value={form.og_image} onChange={(v) => set("og_image", v)} testId="yenisehir-og-image" />
                  <div className="space-y-3">
                    <Input label="Canonical URL (opsiyonel)" value={form.canonical} onChange={(v) => set("canonical", v)} testId="yenisehir-canonical" />
                    <Select
                      label="Schema Türü"
                      value={form.schema_type}
                      onChange={(v) => set("schema_type", v)}
                      options={[
                        { value: "Place", label: "Place (Konum)" },
                        { value: "RealEstateListing", label: "RealEstateListing (Emlak)" },
                        { value: "LocalBusiness", label: "LocalBusiness (İşletme)" },
                      ]}
                      testId="yenisehir-schema-type"
                    />
                    <Input label="Google Maps URL (opsiyonel)" value={form.google_maps_url} onChange={(v) => set("google_maps_url", v)} placeholder="Otomatik üretilir" testId="yenisehir-gmaps" />
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!form.noindex} onChange={(e) => set("noindex", e.target.checked)} data-testid="yenisehir-noindex" />
                      noindex (arama motorlarından gizle)
                    </label>
                  </div>
                </div>
              </Section>
            </div>

            <div className="sticky bottom-0 border-t border-black/10 bg-white px-6 py-4 flex items-center justify-end gap-3">
              <button type="button" onClick={cancel} className="text-sm text-brand-medium hover:text-brand-navy px-4 py-2">Vazgeç</button>
              <Btn type="submit" testId="save-yenisehir-btn-2" disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</Btn>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default AdminYenisehirProjectsPage;
